import { z } from 'zod';
import { PricingTerm, PricingTermArray } from '@/constants/pricingTypes';
import { QuoteKind, QuoteStatus } from '@/constants/quoteTypes';
import { component } from './sceneDataSchema';

// Attribute selection variants
export const declinedAttributeSelection = z.object({
  description: z.string(),
  declined: z.boolean(),
});

export const limitsAttributeSelection = z.object({
  description: z.string(),
  perDay: z.number().nullable(),
  perOccurrence: z.number().nullable(),
  perPerson: z.number().nullable(),
});

export const deductibleAttributeSelection = z.object({
  description: z.string(),
  deductible: z.number(),
});

export const modifierAttributeSelection = z.object({
  description: z.string(),
  id: z.string(),
});

export const attributeSelection = z
  .union([
    modifierAttributeSelection,
    declinedAttributeSelection,
    deductibleAttributeSelection,
    limitsAttributeSelection,
  ])
  .transform((val) => {
    let sortValue = Infinity;

    // Limits (perOccurrence is primary, fallback to perPerson, then perDay)
    if ('perOccurrence' in val && typeof val.perOccurrence === 'number') {
      sortValue = val.perOccurrence;
    } else if ('perPerson' in val && typeof val.perPerson === 'number') {
      sortValue = val.perPerson;
    } else if ('perDay' in val && typeof val.perDay === 'number') {
      sortValue = val.perDay;
    } else if ('deductible' in val && typeof val.deductible === 'number') {
      sortValue = val.deductible;
    }
    // Default for declined/modifier types - keep at end

    return { ...val, sortValue };
  });

export const attributeOption = z.object({
  value: attributeSelection,
  label: z.string(),
});

export const priceDelta = z.object({
  associatedSelection: z.string(),
  description: z.string(),
  amountInCents: z.number(),
});

export const attributeRequirement = z.object({
  fulfilled: z.boolean(),
  kind: z.string(),
  message: z.string().nullable(),
});

export const coverageValidation = z.object({
  valid: z.boolean(),
  message: z.string().nullable(),
  requirements: z.array(attributeRequirement),
});

export const quoteAttribute = z.object({
  kind: z.string(),
  selection: attributeSelection.nullable(),
  selectionOptions: z.array(attributeOption),
  priceDeltas: z.array(priceDelta),
});

export const priceCardDetails = z.object({
  initial_term_selection: z.enum(PricingTerm),
  driver_count: z.number(),
  vehicle_count: z.number(),
  monthly_in_cents: z.number(),
  full_term_in_cents: z.number(),
  full_term_savings_in_cents: z.number(),
  due_today_monthly_in_dollars: z.number(),
  due_today_full_term_in_dollars: z.number(),
  policyholder_first_name: z.string(),
  estimated_quote: z.boolean().optional(),
  quote_source: z.string().optional(),
  quote_kind: z.enum(QuoteKind).optional(),
  quote_status: z.enum(QuoteStatus).optional(),
});

export const coverage = z.object({
  symbol: z.string(),
  scope: z.string(),
  name: z.string().optional().nullable(),
  attributes: z.array(quoteAttribute),
  validation: coverageValidation.optional().default(() => ({
    valid: true,
    message: null,
    requirements: [],
  })),
});

export const quoteState = z.object({
  coverages: z.array(coverage),
  details: priceCardDetails,
  invoice_period: z.enum(PricingTermArray).optional(),
  coverage_rules: z.object().optional(),
  payment_modal_content: z.lazy((): z.ZodArray<typeof component> => z.array(component)),
});

export type QuoteAttribute = z.infer<typeof quoteAttribute>;
export type AttributeSelection = z.infer<typeof attributeSelection>;
export type CoverageDetails = z.infer<typeof priceCardDetails>;
export type CoverageValidation = z.infer<typeof coverageValidation>;
export type QuoteState = z.infer<typeof quoteState>;
export type AttributeOption = z.infer<typeof attributeOption>;
